import { type ChunkSource, type ChunkDataPool, type FileSystem } from '@playcanvas/splat-transform';

import { decodeAttribution, encodeAttribution, type AttributionPayload } from './attribution-data';

export const attributionColumns = async (input: unknown, count: number) => {
    if (input === undefined) return [];
    if (!Array.isArray(input) || input.length > 32) throw new Error('Invalid SOG attribution channels.');
    const decoded = await Promise.all(input.map(p => decodeAttribution(p, count)));
    const names = new Set(decoded.map(p => p.payload.property));
    if (names.size !== decoded.length || names.has('state')) throw new Error('Duplicate or reserved attribution channel.');
    return decoded;
};

export const restoreSogAttribution = async (source: ChunkSource, input: unknown): Promise<ChunkSource> => {
    try {
        const channels = await attributionColumns(input, source.meta.numGaussians);
        if (!channels.length) return source;
        const fields = Object.fromEntries(channels.map((p, i) => [p.payload.property, { type: 'float32' as const, byteOffset: i * 4, components: 1 }]));
        const stride = channels.length * 4;
        return {
            meta: { ...source.meta,
                extraColumns: channels.map(p => ({ name: p.payload.property, type: 'float32' as const })),
                availableLayers: new Set([...source.meta.availableLayers, 'other']),
                layouts: { ...source.meta.layouts, other: { fields, stride } } },
            async read(request) {
                if (request.position || request.geometric || request.color) await source.read({ ...request, other: undefined });
                if (!request.other) return;
                const view = new DataView(request.other.data);
                const count = 'indices' in request ? request.count : request.other.count;
                for (let i = 0; i < count; i++) {
                    const row = 'indices' in request ? request.indices[request.indexOffset + i] : request.chunkIndex * source.meta.chunkSize + i;
                    for (let c = 0; c < channels.length; c++) {
                        const { payload, values } = channels[c];
                        const value = payload.encoding === 'sh0' ? values[row] : (values[row] - 0.5) / 0.28209479177387814;
                        view.setFloat32(i * request.other.stride + c * 4, value, true);
                    }
                }
            },
            close: () => source.close()
        };
    } catch (error) {
        await source.close();
        throw error;
    }
};

/** Gather AFTER filtering, duplication, LOD generation and the final SOG permutation. */
export const gatherSogAttribution = async (source: ChunkSource, pool: ChunkDataPool, order: Uint32Array): Promise<AttributionPayload[]> => {
    const layout = source.meta.layouts.other;
    if (!layout || !source.meta.extraColumns.length) return [];
    if (order.length * 4 > 64 * 1024 * 1024) throw new Error('Attribution channel exceeds 64 MiB; use streamed LOD export.');
    if (order.length !== source.meta.numGaussians) throw new Error('Attribution output ordering has the wrong length.');
    const result: AttributionPayload[] = [];
    for (const column of source.meta.extraColumns) {
        if (column.name === 'state') continue;
        const field = layout.fields[column.name];
        if (!field || field.type !== 'float32') throw new Error('Attribution export requires float32 columns.');
        const values = new Float32Array(order.length);
        for (let offset = 0; offset < order.length; offset += source.meta.chunkSize) {
            const count = Math.min(source.meta.chunkSize, order.length - offset);
            const other = pool.acquire('other', layout, count);
            try {
                for (let i = offset; i < offset + count; i++) if (order[i] >= source.meta.numGaussians) throw new Error('Invalid attribution row mapping.');
                await source.read({ indices: order, indexOffset: offset, count, other });
                const view = new DataView(other.data);
                for (let i = 0; i < count; i++) values[offset + i] = view.getFloat32(i * other.stride + field.byteOffset, true);
            } finally {
                other.release();
            }
        }
        result.push(await encodeAttribution(column.name, values));
    }
    return result;
};

const exported = new WeakMap<FileSystem, Map<string, AttributionPayload[]>>();
export const getSogAttribution = (fs: FileSystem, filename: string) => exported.get(fs)?.get(filename) ?? [];
export const attributionFileSystem = <T extends FileSystem>(fs: T) => Object.assign(fs, {
    jsiSogAttribution: async (source: ChunkSource, pool: ChunkDataPool, order: Uint32Array, filename: string) => {
        const payloads = await gatherSogAttribution(source, pool, order);
        let files = exported.get(fs);
        if (!files) exported.set(fs, files = new Map());
        files.set(filename, payloads);
        return payloads;
    }
});
